<template>
  <textarea
    :id="props.id"
    v-model="inputValue"
    class="tw-block tw-w-full tw-rounded tw-border tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all"
    :class="[classes]"
    :name="props.name"
    :rows="props.rows"
    :maxlength="props.maxLength"
    :autofocus="props.autofocus"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :data-test-id="props.testId"
    @blur="emitBlur"
  />
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue";
import type { Size } from "../../components/types";

export interface FormInputProps {
  id: string;
  name: string;
  value: string | number;
  placeholder?: string;
  state?: boolean | null;
  disabled?: boolean;
  readonly?: boolean;
  autofocus?: boolean;
  testId?: string;
  rows?: number;
  maxLength?: number;
  size?: Size;
}

const props = withDefaults(defineProps<FormInputProps>(), {
  size: "medium",
  placeholder: "",
  state: null,
  disabled: false,
  readonly: false,
  autofocus: false,
  rows: 3,
  testId: "",
  maxLength: undefined,
});

const emit = defineEmits<{
  (event: "update:value", value: string | number): void;
  (event: "on-blur"): void;
}>();

const inputValue = ref(props.value);

const sizes = {
  small: "tw-text-sm tw-py-1 tw-px-3",
  medium: "tw-text-base tw-py-2 tw-px-3",
  large: "tw-text-lg tw-py-3 tw-px-3",
};

const states = {
  none: "tw-border-dorian-gray-100/80",
  valid: "tw-border-success-500",
  error: "tw-border-danger-500",
};

const focusClasses =
  "focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10";

const disabledClasses =
  "disabled:tw-cursor-not-allowed disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none";

const readonlyClasses =
  "read-only:tw-cursor-not-allowed read-only:tw-bg-dorian-gray-50 read-only:tw-text-dorian-gray-500 read-only:tw-outline-none";

const classes = computed(() => {
  return [
    sizes[props.size],
    props.state === null
      ? states.none
      : props.state
        ? states.valid
        : states.error,
    focusClasses,
    disabledClasses,
    readonlyClasses,
  ];
});

watch(
  () => props.value,
  () => {
    inputValue.value = props.value;
  },
);

watch(inputValue, (newValue) => {
  emit("update:value", newValue);
});

const emitBlur = (): void => {
  if (typeof inputValue.value === "string") {
    inputValue.value = inputValue.value.trim();
  }
  emit("on-blur");
};
</script>

<style scoped></style>
